/**
 * useSortableTable — shared hook for sortable tables.
 *
 * HOUSE RULE: every data table in this app has sortable columns by default.
 * Use this hook. Do not write bespoke table sorting.
 *
 * Usage:
 *   const { sortedRows, sortableCell } = useSortableTable(rows, {
 *     defaultKey: 'name',
 *     defaultDir: 'asc',
 *   });
 *   ...
 *   <TableHead>
 *     <TableRow>
 *       {sortableCell('name', 'Name')}
 *       {sortableCell('created_at', 'Created', 'right')}
 *     </TableRow>
 *   </TableHead>
 *   ...
 *   {sortedRows.map(...)}
 */

import React, { useState, useMemo } from 'react';
import { TableCell, TableSortLabel } from '@mui/material';

function normForCompare(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  const s = String(v).trim();
  // ISO-ish dates sort correctly as strings once lowercased
  // Phone/extension: treat as number when it looks like one
  if (/^[\d\s().+\-x]+$/.test(s) && /\d/.test(s)) {
    const digits = s.replace(/\D/g, '');
    if (digits) return parseInt(digits.slice(0, 15), 10);
  }
  return s.toLowerCase();
}

export function stableSort(arr, key, direction) {
  if (!Array.isArray(arr)) return [];
  if (!key) return arr;
  const sign = direction === 'desc' ? -1 : 1;
  return arr
    .map((item, idx) => [item, idx])
    .sort((a, b) => {
      const av = normForCompare(a[0] && a[0][key]);
      const bv = normForCompare(b[0] && b[0][key]);
      if (av < bv) return -1 * sign;
      if (av > bv) return 1 * sign;
      return a[1] - b[1];
    })
    .map((pair) => pair[0]);
}

export function useSortableTable(rows, { defaultKey = null, defaultDir = 'asc' } = {}) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedRows = useMemo(
    () => stableSort(rows || [], sortKey, sortDir),
    [rows, sortKey, sortDir]
  );

  const sortableCell = (key, label, align, sxOverride) => (
    <TableCell
      align={align}
      sortDirection={sortKey === key ? sortDir : false}
      sx={{ fontWeight: 700, ...(sxOverride || {}) }}
    >
      <TableSortLabel
        active={sortKey === key}
        direction={sortKey === key ? sortDir : 'asc'}
        onClick={() => onSort(key)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return { sortedRows, sortableCell, sortKey, sortDir, onSort, setSortKey, setSortDir };
}

export default useSortableTable;
